import { useEffect, useState } from "react"
import { Link, router } from "@inertiajs/react"
import {
  ArrowRight,
  Check,
  Clock,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  DollarSign,
  Download,
  FileText,
  Lock,
  Receipt,
  Search,
  ExternalLink,
  X,
} from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"

// Atrium item 8 — the customer Billing & receipts hub: a net-new, READ-ONLY
// presentation of REAL payment data (CustomerBillingPresenter). Every amount is
// prop-driven — the tax-inclusive totals, the real Tax line, the per-receipt
// breakdown — nothing is hardcoded (P-001). There is NO customer card vault, so
// the page shows "Paid via {gateway}" + a "How you pay" explainer, never saved
// cards. Every Pay now action posts a server-owned target to the same authorized
// checkout endpoint. Hosted URLs never enter this page.

const POST_PROJECT_HREF = "/post"

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "due", label: "Due" },
  { key: "paid", label: "Paid" },
]

function money(value) {
  return `$${Number(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function formatDate(iso) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

function StatTile({ icon: Icon, label, value, sub, tone }) {
  const due = tone === "due"
  return (
    <div className="rounded-[13px] border border-border bg-card px-[18px] py-4">
      <div className="flex items-center gap-[7px] text-[11.5px] font-bold tracking-[0.05em] text-ink-3 uppercase">
        <Icon className={due ? "size-[14px] text-needs-you" : "size-[14px] text-ink-3"} strokeWidth={2} />
        {label}
      </div>
      <div
        className={
          due
            ? "mt-2 font-display text-[27px] leading-none font-bold tracking-[-1px] text-needs-you"
            : "mt-2 font-display text-[27px] leading-none font-bold tracking-[-1px] text-foreground"
        }
      >
        {money(value)}
      </div>
      {sub && <div className="mt-[3px] text-[11.5px] text-ink-3">{sub}</div>}
    </div>
  )
}

function SummaryStrip({ summary, receiptsTotal }) {
  return (
    <section aria-label="Account-wide billing summary">
      <p className="mb-2 text-[11px] font-bold tracking-[0.06em] text-ink-3 uppercase">
        Account-wide totals · filters below do not change these figures
      </p>
      {/* P8-H7 "Muster" — `.bl-sum` is 1-col at ≤700 (design/spec/account/billing.html
          `@media (max-width:700px)`). `sm:` (640) showed three tiles across the
          641–700 band the design rules to one; `min-[701px]` compiles to
          `@media (width >= 701px)`. */}
      <div data-testid="billing-summary" className="grid grid-cols-1 gap-3 min-[701px]:grid-cols-3">
        <StatTile
          icon={Check}
          label="Total paid"
          value={summary?.totalPaid}
          sub={receiptsTotal > 0 ? `${receiptsTotal} receipt${receiptsTotal === 1 ? "" : "s"} · account lifetime` : "No payments yet"}
        />
        <StatTile
          icon={Clock}
          label="Due now"
          value={summary?.dueNow}
          tone="due"
          sub={summary?.dueCount > 0 ? `${summary.dueCount} charge${summary.dueCount === 1 ? "" : "s"} · account-wide` : "Nothing outstanding"}
        />
        <StatTile
          icon={Receipt}
          label="Total billed"
          value={summary?.totalBilled}
          sub={receiptsTotal > 0 || summary?.dueCount > 0 ? "across all projects" : "—"}
        />
      </div>
    </section>
  )
}

function BillingToolbar({ filters }) {
  const [query, setQuery] = useState(filters.q ?? "")

  useEffect(() => {
    setQuery(filters.q ?? "")
  }, [filters.q])

  function visit(next = {}) {
    const q = next.q ?? filters.q ?? ""
    const status = next.status ?? filters.status ?? "all"
    router.get(
      "/billing",
      { ...(q ? { q } : {}), ...(status !== "all" ? { status } : {}), page: next.page ?? 1 },
      { preserveScroll: true, preserveState: true }
    )
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center">
      <form
        className="relative min-w-0 flex-1"
        onSubmit={(event) => {
          event.preventDefault()
          visit({ q: query.trim(), page: 1 })
        }}
      >
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-3" />
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search project title or order number"
          aria-label="Search billing history"
          className="pl-9"
        />
      </form>
      <div className="flex items-center gap-1 rounded-xl bg-secondary p-1" aria-label="Billing status">
        {STATUS_FILTERS.map((option) => (
          <button
            key={option.key}
            type="button"
            aria-pressed={filters.status === option.key}
            onClick={() => visit({ q: query.trim(), status: option.key, page: 1 })}
            className={
              filters.status === option.key
                ? "rounded-lg bg-card px-3 py-1.5 text-xs font-bold text-foreground shadow-sm"
                : "rounded-lg px-3 py-1.5 text-xs font-semibold text-ink-3 hover:text-foreground"
            }
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function DueNowBanner({ rows }) {
  if (!rows.length) return null

  function pay(row) {
    router.post(row.payPath, {
      targetKind: row.target?.kind,
      targetId: row.target?.id,
    })
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border border-l-[3px] border-l-needs-you bg-card">
      <div className="flex items-center gap-[10px] px-5 pt-[14px] pb-3">
        <span className="grid size-[30px] shrink-0 place-items-center rounded-[9px] bg-[color-mix(in_oklab,var(--needs-you)_12%,transparent)]">
          <Clock className="size-4 text-needs-you" strokeWidth={2.2} />
        </span>
        <div>
          <h3 className="font-display text-[14.5px] font-bold text-foreground">Payment due to start your project</h3>
          <p className="mt-px text-[11.5px] text-ink-3">
            You only pay once a writer accepts. Tax is added at checkout.
          </p>
        </div>
      </div>

      {/* P8-H7 "Muster" — the ruled ≤700 due-row treatment, verbatim from
          design/spec/account/billing.html: `.due-row{flex-wrap:wrap}` +
          `.due-row .pay{width:100%;justify-content:center}`. Tailwind v4 compiles
          `max-[701px]` to `@media (width < 701px)`, i.e. exactly "at or below 700"
          (the same exclusive-literal convention as PriceRail's RAIL_CARD_BAND), so
          nothing below declares a single property at 701 and up: the desktop row
          is untouched. */}
      {rows.map((row) => (
        <div
          key={row.key}
          data-testid="due-row"
          className="flex items-center gap-[14px] border-t border-border px-5 py-[13px] max-[701px]:flex-wrap"
        >
          <span className="grid size-9 shrink-0 place-items-center rounded-[9px] bg-secondary">
            <DollarSign className="size-[18px] text-muted-foreground" strokeWidth={1.9} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13.5px] font-semibold text-foreground">{row.title}</div>
            <div className="mt-[2px] text-[11.5px] text-ink-3">{row.label}</div>
          </div>
          <div data-testid="due-amount" className="shrink-0 text-right">
            <div className="font-display text-[17px] font-bold text-foreground">{money(row.amount)}</div>
            <div className="mt-px text-[10.5px] text-ink-3">+ tax at checkout</div>
          </div>
          <button
            type="button"
            data-testid="due-pay"
            onClick={() => pay(row)}
            disabled={!row.payPath || !row.target}
            className="inline-flex shrink-0 items-center gap-[7px] rounded-[10px] bg-primary px-4 py-[10px] text-[13px] font-bold text-white transition-colors hover:bg-red-dark disabled:opacity-50 max-[701px]:w-full max-[701px]:justify-center"
          >
            <DollarSign className="size-[15px]" strokeWidth={2.3} />
            Pay now
          </button>
        </div>
      ))}
    </div>
  )
}

function StatusPill({ status }) {
  const paid = status === "paid"
  return (
    <span
      className={
        paid
          ? "inline-flex items-center gap-[5px] rounded-full bg-[color-mix(in_oklab,var(--done)_13%,transparent)] px-[9px] py-[3px] text-[10.5px] font-bold text-done"
          : "inline-flex items-center gap-[5px] rounded-full bg-[color-mix(in_oklab,var(--needs-you)_13%,transparent)] px-[9px] py-[3px] text-[10.5px] font-bold text-needs-you"
      }
    >
      {paid ? <Check className="size-[11px]" strokeWidth={2.6} /> : <span className="size-[6px] rounded-full bg-current" />}
      {paid ? "Paid" : "Pending"}
    </span>
  )
}

// P8-H7 "Muster" — at ≤700 the receipt row becomes the ruled TWO-LINE stack:
// line 1 is the project title (full width, with its #order · date · Paid-via meta
// beneath it), line 2 is icon · pill · amount · download. That is
// design/spec/account/billing.html's `@media (max-width:700px)` block translated
// property for property — `.rcpt{flex-wrap:wrap;row-gap:6px}`,
// `.rcpt .rm{flex:1 1 100%;order:-1}`, `.rcpt .rs{width:auto}`,
// `.rcpt .ra{margin-left:auto;order:2}`, `.rcpt .rdl{order:3}`. Tailwind v4
// compiles `max-[701px]` to `@media (width < 701px)` = "at or below 700", so the
// row at 701 and up keeps every one of its original declarations. Nothing is
// dropped at any width and the whole row stays the single click target that opens
// the receipt dialog (the download link still stops propagation).
//
// THE SECOND BAND (conductor ruling, P8-H7 slice 2). Moving `.bl-grid` from 1024
// down to 861 starves this row a second time: at 861–1023 it lives in a ~340–430px
// grid column, NARROWER than it is at a 700px viewport, and the one-line layout
// truncated the project title to "Quic…" with the meta fragmented over five lines.
// So every ≤700 stack utility below carries a twin scoped to exactly the band this
// slice newly turned two-column: `min-[861px]:max-[1024px]:*`, which Tailwind v4
// nests as `@media (width >= 861px) { @media (width < 1024px) { … } }` — inclusive
// low edge, exclusive high edge, i.e. 861 through 1023. At 1024 and up the row was
// ALREADY two-column before this slice (`lg:` is min-width 1024) with a title near
// 158px; that is the shipped status quo and is deliberately left alone.
function ReceiptRow({ receipt, onOpen }) {
  return (
    <div
      data-testid="receipt-row"
      className="flex cursor-pointer items-center gap-[14px] border-b border-border px-5 py-[13px] transition-colors last:border-b-0 hover:bg-secondary max-[701px]:flex-wrap max-[701px]:gap-y-[6px] min-[861px]:max-[1024px]:flex-wrap min-[861px]:max-[1024px]:gap-y-[6px]"
      onClick={() => onOpen(receipt)}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-[9px] bg-secondary">
        <Receipt className="size-[17px] text-muted-foreground" strokeWidth={1.9} />
      </span>
      <div className="min-w-0 flex-1 max-[701px]:-order-1 max-[701px]:basis-full min-[861px]:max-[1024px]:-order-1 min-[861px]:max-[1024px]:basis-full">
        <div data-testid="receipt-title" className="truncate text-[13.5px] font-semibold text-foreground">
          {receipt.projects.join(", ")}
        </div>
        <div className="mt-[2px] flex flex-wrap items-center gap-[7px] text-[11.5px] text-ink-3">
          <span>#{receipt.orderNumber}</span>
          <span className="size-[3px] rounded-full bg-current opacity-50" />
          <span>{formatDate(receipt.date)}</span>
          {receipt.method && (
            <>
              <span className="size-[3px] rounded-full bg-current opacity-50" />
              <span className="inline-flex items-center gap-1">
                <Lock className="size-3" strokeWidth={2} />
                Paid via {receipt.method}
              </span>
            </>
          )}
        </div>
      </div>
      <div
        data-testid="receipt-amount"
        className="shrink-0 text-right font-display text-[15px] font-bold text-foreground max-[701px]:order-2 max-[701px]:ml-auto min-[861px]:max-[1024px]:order-2 min-[861px]:max-[1024px]:ml-auto"
      >
        {money(receipt.amount)}
      </div>
      <div
        data-testid="receipt-status"
        className="w-[70px] shrink-0 text-right max-[701px]:w-auto min-[861px]:max-[1024px]:w-auto"
      >
        <StatusPill status={receipt.status} />
      </div>
      {receipt.downloadUrl ? (
        <a
          href={receipt.downloadUrl}
          data-testid="receipt-download"
          onClick={(e) => e.stopPropagation()}
          title="Download receipt"
          aria-label="Download receipt"
          className="grid size-[30px] shrink-0 place-items-center rounded-lg border border-line-2 bg-card text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground max-[701px]:order-3 min-[861px]:max-[1024px]:order-3"
        >
          <Download className="size-[15px]" strokeWidth={2} />
        </a>
      ) : (
        <span className="w-[30px] shrink-0 max-[701px]:order-3 min-[861px]:max-[1024px]:order-3" />
      )}
    </div>
  )
}

function EmptyReceipts() {
  return (
    <div className="flex flex-col items-center px-6 py-11 text-center">
      <span className="mb-[14px] grid size-12 place-items-center rounded-[13px] border border-border bg-secondary text-ink-3">
        <Receipt className="size-[23px]" strokeWidth={1.7} />
      </span>
      <h4 className="font-display text-[14.5px] font-bold text-foreground">No receipts yet</h4>
      <p className="mt-[6px] max-w-[330px] text-[12.5px] leading-[1.5] text-ink-3">
        When you pay for a project, your receipts and invoices will appear here — ready to download anytime.
      </p>
      <Link
        href={POST_PROJECT_HREF}
        className="mt-[15px] inline-flex items-center gap-[7px] rounded-[10px] bg-primary px-4 py-[10px] text-[13px] font-bold text-white no-underline transition-colors hover:bg-red-dark"
      >
        Start a project
        <ArrowRight className="size-[15px]" strokeWidth={2.3} />
      </Link>
    </div>
  )
}

function ReceiptsCard({ receipts, receiptsTotal, filters, onOpen }) {
  const empty = receipts.length === 0
  const filtered = Boolean(filters.q) || filters.status !== "all"

  function pageHref(page) {
    const params = new URLSearchParams()
    if (filters.q) params.set("q", filters.q)
    if (filters.status !== "all") params.set("status", filters.status)
    params.set("page", String(page))
    return `/billing?${params.toString()}`
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-[15px]">
        <h3 className="flex items-center gap-[9px] font-display text-[14.5px] font-bold text-foreground">
          <Receipt className="size-[17px] text-primary" strokeWidth={1.9} />
          Receipts &amp; invoices
        </h3>
        {receiptsTotal > 0 && (
          <span className="text-[11px] text-ink-3">
            {receiptsTotal} matching receipt{receiptsTotal === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {empty ? (
        filtered ? (
          <div className="px-6 py-10 text-center text-[12.5px] text-ink-3">
            No paid receipts match these filters.
          </div>
        ) : (
          <EmptyReceipts />
        )
      ) : (
        receipts.map((receipt) => (
          <ReceiptRow key={`${receipt.orderId}-${receipt.orderNumber}`} receipt={receipt} onOpen={onOpen} />
        ))
      )}

      {filters.pages > 1 && (
        <nav aria-label="Receipt pages" className="flex items-center justify-between border-t border-border px-5 py-3">
          <span className="text-xs text-ink-3">
            Page {filters.page} of {filters.pages}
          </span>
          <div className="flex gap-2">
            {filters.page > 1 && (
              <Link
                href={pageHref(filters.page - 1)}
                preserveScroll
                className="inline-flex items-center gap-1 rounded-lg border border-line-2 px-3 py-1.5 text-xs font-semibold text-foreground no-underline"
              >
                <ChevronLeft className="size-3.5" /> Previous
              </Link>
            )}
            {filters.page < filters.pages && (
              <Link
                href={pageHref(filters.page + 1)}
                preserveScroll
                className="inline-flex items-center gap-1 rounded-lg border border-line-2 px-3 py-1.5 text-xs font-semibold text-foreground no-underline"
              >
                Next <ChevronRight className="size-3.5" />
              </Link>
            )}
          </div>
        </nav>
      )}
    </div>
  )
}

function HowYouPayCard() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center gap-[9px] border-b border-border px-5 py-[15px]">
        <Lock className="size-[17px] text-primary" strokeWidth={1.9} />
        <h3 className="font-display text-[14.5px] font-bold text-foreground">How you pay</h3>
      </div>
      <div className="flex gap-[10px] px-5 py-[15px]">
        <CreditCard className="mt-px size-[15px] shrink-0 text-ink-3" strokeWidth={2} />
        <p className="text-[11.5px] leading-[1.55] text-ink-3">
          Dibbly <b className="font-semibold text-muted-foreground">doesn&apos;t store your card</b>. When a freelancer accepts your
          project, you&apos;ll pay on a <b className="font-semibold text-muted-foreground">secure checkout page</b> hosted by our
          payment processor (Stripe / Shopify) — enter or pick a card right there. Every payment shows up here as a
          receipt.
        </p>
      </div>
    </div>
  )
}

function LineItem({ label, sub, value, tone }) {
  const toneClass = tone === "credit" ? "text-done" : "text-foreground"
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 text-[13px]">
      <span className="text-muted-foreground">
        {label}
        {sub && <small className="mt-[2px] block text-[11px] text-ink-3">{sub}</small>}
      </span>
      <span className={`font-display font-semibold whitespace-nowrap ${toneClass}`}>{value}</span>
    </div>
  )
}

function ReceiptModal({ receipt, onClose }) {
  // AT-06: the open-state and the body read the SAME condition. `open` used to
  // key off `receipt` alone while the body also required `detail`, so a
  // detail-less receipt opened a bare dialog whose only close control lived
  // inside the detail-gated body. CustomerBillingPresenter#build_receipt always
  // emits `detail`, so that shape is defensive-only — gate the whole modal on
  // it and a degraded receipt now opens nothing at all.
  const detail = receipt?.detail
  const open = receipt != null && detail != null

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-[520px]" showCloseButton={false}>
        {open && (
          <>
            <DialogHeader className="flex-row items-start justify-between gap-4">
              <div>
                <div className="text-[11px] font-bold tracking-[0.05em] text-ink-3 uppercase">
                  {receipt.status === "paid" ? "Receipt" : "Invoice"} · #{receipt.orderNumber}
                </div>
                <DialogTitle className="mt-1 font-display text-[18px] tracking-[-0.4px]">
                  {receipt.projects.join(", ")}
                </DialogTitle>
                <div className="mt-1 text-[12px] text-ink-3">{formatDate(receipt.date)}</div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="grid size-[30px] shrink-0 place-items-center rounded-lg border border-line-2 bg-card text-ink-3 transition-colors hover:bg-secondary hover:text-foreground"
              >
                <X className="size-4" strokeWidth={2.2} />
              </button>
            </DialogHeader>

            <div className="pt-1">
              {detail.lineItems.map((item, i) => (
                <LineItem key={i} label={item.label} value={money(item.amount)} />
              ))}

              {detail.discount && (
                <LineItem
                  label={`Discount${detail.discount.code ? ` ${detail.discount.code}` : ""}`}
                  sub={detail.discount.percent ? `${detail.discount.percent} off` : null}
                  value={`−${money(detail.discount.amount)}`}
                  tone="credit"
                />
              )}

              {detail.reward && (
                <LineItem
                  label="Dibbly Rewards"
                  sub={`${Number(detail.reward.points).toLocaleString("en-US")} pts · 200 pts = $1`}
                  value={`−${money(detail.reward.amount)}`}
                  tone="credit"
                />
              )}

              <LineItem label="Tax" value={money(detail.tax)} />

              <div className="my-2 h-px bg-border" />

              <div className="flex items-baseline justify-between gap-4 py-2">
                <span className="font-display text-[14px] font-bold text-foreground">
                  {receipt.status === "paid" ? "Total paid" : "Total due"}
                </span>
                <span className="font-display text-[21px] font-bold tracking-[-0.6px] text-foreground">
                  {money(detail.total)}
                </span>
              </div>

              {detail.gateway && (
                <div className="mt-3 flex items-center gap-[9px] rounded-[10px] bg-secondary px-[14px] py-[11px] text-[12px] text-muted-foreground">
                  <Check className="size-[15px] text-done" strokeWidth={2.2} />
                  Paid via {detail.gateway}
                </div>
              )}

              {receipt.downloadUrl && (
                <div className="mt-4 flex items-center gap-2">
                  <a
                    href={receipt.downloadUrl}
                    className="inline-flex flex-1 items-center justify-center gap-[7px] rounded-[10px] bg-primary px-4 py-[11px] text-[13px] font-bold text-white no-underline transition-colors hover:bg-red-dark"
                  >
                    <Download className="size-[15px]" strokeWidth={2.2} />
                    Download receipt (PDF)
                  </a>
                  <Link
                    href={`/projects/${receipt.orderId}`}
                    className="inline-flex items-center justify-center gap-[7px] rounded-[10px] border border-line-2 bg-card px-4 py-[11px] text-[13px] font-semibold text-muted-foreground no-underline transition-colors hover:bg-secondary hover:text-foreground"
                  >
                    <ExternalLink className="size-[15px]" strokeWidth={2} />
                    View order
                  </Link>
                </div>
              )}

              <div className="mt-4 flex gap-[10px] border-t border-border pt-3">
                <FileText className="mt-px size-[14px] shrink-0 text-ink-3" strokeWidth={2} />
                <p className="text-[11px] leading-[1.5] text-ink-3">
                  This is a summary for your records. Shopify&apos;s emailed invoice remains the formal tax document for
                  this purchase.
                </p>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default function Billing({ billing }) {
  const [openReceipt, setOpenReceipt] = useState(null)

  const summary = billing?.summary ?? { totalPaid: 0, dueNow: 0, totalBilled: 0 }
  const dueNow = billing?.dueNow ?? []
  const receipts = billing?.receipts ?? []
  const receiptsTotal = billing?.receiptsTotal ?? receipts.length
  const accountReceiptsTotal = billing?.accountReceiptsTotal ?? receiptsTotal
  const filters = billing?.filters ?? { q: "", status: "all", page: 1, pages: 1, paidTotal: receiptsTotal, dueTotal: dueNow.length }
  const hasMatches = dueNow.length > 0 || receipts.length > 0

  return (
    <>
      <div className="mx-auto flex w-full max-w-[1020px] flex-col gap-5 px-[30px] pt-6 pb-16">
        <div>
          <h1 className="font-display text-[23px] font-bold tracking-[-0.5px] text-foreground">Billing &amp; receipts</h1>
          <p className="mt-[3px] max-w-[580px] text-[13px] leading-[1.5] text-ink-3">
            Review what you&apos;ve paid and download receipts. Posting a project is free — you&apos;re only charged once
            a freelancer accepts.
          </p>
        </div>

        <SummaryStrip summary={summary} receiptsTotal={accountReceiptsTotal} />

        <BillingToolbar filters={filters} />

        {!hasMatches && (filters.q || filters.status !== "all") && (
          <div role="status" className="rounded-xl border border-dashed border-border bg-card px-5 py-6 text-center text-sm text-ink-3">
            No billing results match your search and status filters.
          </div>
        )}

        <DueNowBanner rows={dueNow} />

        {/* P8-H7 "Muster" — `.bl-grid` is 1-col at ≤860; 861 is the first
            two-column width (`@media (width >= 861px)`), not `lg:`'s 1024. */}
        <div
          data-testid="billing-grid"
          className="grid grid-cols-1 items-start gap-5 min-[861px]:grid-cols-[1.55fr_1fr]"
        >
          <div className="flex min-w-0 flex-col gap-5">
            {filters.status !== "due" && (
              <ReceiptsCard
                receipts={receipts}
                receiptsTotal={receiptsTotal}
                filters={filters}
                onOpen={setOpenReceipt}
              />
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-5">
            <HowYouPayCard />
          </div>
        </div>
      </div>

      <ReceiptModal receipt={openReceipt} onClose={() => setOpenReceipt(null)} />
    </>
  )
}

Billing.layout = (page) => (
  <AppShell active="Billing" crumb={["Billing"]}>
    {page}
  </AppShell>
)
